/**
 * How a stored campaign maps onto the builder's trigger conditions, and the
 * read-only summary the conditions card renders from them. The vocabulary
 * itself (`WHERE`, `VISITOR_TYPES`, `DEVICE_TYPES`, `FREQUENCIES`,
 * `AVAILABILITIES`) lives in `campaignBuilder` beside the publish validation
 * that reads it.
 */
import {
  AVAILABILITIES,
  DEVICE_TYPES,
  FREQUENCIES,
  VISITOR_TYPES,
  WHERE,
} from './campaignBuilder';

export const DEFAULT_CONDITIONS = {
  where: WHERE.EVERYWHERE,
  visitorType: VISITOR_TYPES.ALL,
  deviceType: DEVICE_TYPES.ALL,
  frequency: FREQUENCIES.EVERY_VISIT,
  availability: AVAILABILITIES.ALWAYS,
};

export const whereFromCampaign = record =>
  record?.trigger_rules?.url ? WHERE.SPECIFIC_URL : WHERE.EVERYWHERE;

/**
 * `trigger_rules.availability` is the current home for "when does this fire".
 * Campaigns authored before it existed only carry the legacy boolean
 * `trigger_only_during_business_hours`, so that is what they map from.
 */
export const availabilityFromCampaign = record => {
  const { availability } = record?.trigger_rules ?? {};
  if (availability) return availability;

  return record?.trigger_only_during_business_hours
    ? AVAILABILITIES.BUSINESS_HOURS
    : AVAILABILITIES.ALWAYS;
};

const I18N_ROOT = 'CAMPAIGN.BUILDER.CONDITIONS';

/**
 * The conditions card's read-out: the `Where` value plus one chip per
 * remaining condition, in the order the modal presents them.
 *
 * Returns i18n keys and their interpolation params rather than translated
 * strings, so the summary stays pure and testable.
 *
 * @returns {{where: {i18nKey: string, params: object},
 *            chips: Array<{key: string, icon: string, i18nKey: string, params: object}>}}
 */
export const buildConditionsSummary = ({
  where = DEFAULT_CONDITIONS.where,
  url = '',
  visitorType = DEFAULT_CONDITIONS.visitorType,
  deviceType = DEFAULT_CONDITIONS.deviceType,
  frequency = DEFAULT_CONDITIONS.frequency,
  availability = DEFAULT_CONDITIONS.availability,
  timeOnPage = 0,
} = {}) => ({
  where:
    where === WHERE.SPECIFIC_URL && url
      ? { i18nKey: `${I18N_ROOT}.WHERE.SUMMARY_URL`, params: { url } }
      : { i18nKey: `${I18N_ROOT}.WHERE.OPTIONS.EVERYWHERE`, params: {} },
  chips: [
    {
      key: 'visitorType',
      icon: 'i-lucide-users',
      i18nKey: `${I18N_ROOT}.WHO.OPTIONS.${visitorType.toUpperCase()}`,
      params: {},
    },
    {
      key: 'deviceType',
      icon: 'i-lucide-monitor-smartphone',
      i18nKey: `${I18N_ROOT}.DEVICES.OPTIONS.${deviceType.toUpperCase()}`,
      params: {},
    },
    {
      key: 'frequency',
      icon: 'i-lucide-repeat',
      i18nKey: `${I18N_ROOT}.FREQUENCY.OPTIONS.${frequency.toUpperCase()}`,
      params: {},
    },
    {
      key: 'availability',
      icon: 'i-lucide-clock',
      i18nKey: `${I18N_ROOT}.WHEN.OPTIONS.${availability.toUpperCase()}`,
      params: {},
    },
    {
      key: 'timeOnPage',
      icon: 'i-lucide-timer',
      i18nKey: `${I18N_ROOT}.DELAY.CHIP`,
      params: { seconds: Number(timeOnPage) || 0 },
    },
  ],
});
